import { useCallback, useEffect, useMemo, useState } from "react";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import { readTabFromHash, resolveActiveTab, writeTabToHash } from "./tabHelpers";

// État des onglets d'une fiche : onglet demandé (mémorisé par utilisateur,
// synchronisé avec le hash d'URL en mode page) et onglet effectif (repli sur le
// premier visible si l'onglet demandé est masqué).
export default function useFicheTabs({
  tabs = [],
  defaultId = null,
  storageKey = null,
  syncHash = false,
}) {
  const visibleTabs = useMemo(() => tabs.filter((t) => !t.hidden), [tabs]);
  const ids = useMemo(() => tabs.map((t) => t.id), [tabs]);

  const [persisted, setPersisted] = usePersistedState(
    storageKey || "espoar.boa.fiche.tab.volatile",
    defaultId
  );
  const [requested, setRequested] = useState(() => {
    const fromHash = syncHash ? readTabFromHash(window.location.hash, ids, null) : null;
    return fromHash || (storageKey ? persisted : defaultId) || defaultId;
  });

  const activeId = resolveActiveTab(requested, visibleTabs);

  const select = useCallback(
    (id) => {
      setRequested(id);
      if (storageKey) setPersisted(id);
      if (syncHash) writeTabToHash(id);
    },
    [storageKey, syncHash, setPersisted]
  );

  // Le hash ne reflète que l'onglet effectivement affiché.
  useEffect(() => {
    if (syncHash && activeId && readTabFromHash(window.location.hash, ids, null) !== activeId) {
      writeTabToHash(activeId);
    }
  }, [syncHash, activeId, ids]);

  return { activeId, requestedId: requested, select, visibleTabs };
}
